import { PDFDocument, StandardFonts } from 'pdf-lib';

const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;
const PAGE_MARGIN = 42;
const DEFAULT_FONT_SIZE = 10.5;
const HEADER_FONT_SIZE = 16;
const QUESTION_FONT_SIZE = 11.5;
const LINE_HEIGHT = 15;
const QUESTION_SPACING = 8;
const SECTION_SPACING = 14;
const MIN_BOTTOM_MARGIN = 48;

const normalizeAnswerValue = (value) => {
	if (value === true) {
		return 'Yes';
	}

	if (value === false) {
		return 'No';
	}

	if (Array.isArray(value)) {
		if (!value.length) {
			return 'No selection';
		}

		return value.join(', ');
	}

	if (value === null || value === undefined || value === '') {
		return 'No answer';
	}

	if (typeof value === 'string') {
		return value;
	}

	return String(value);
};

// Standard PDF fonts use WinAnsi. Preserve submitted answers in data while replacing
// only glyphs the printable PDF copy cannot encode, so form submission never crashes.
const makeTextSafeForFont = ({ text, font }) => {
	const supportedCodePoints = new Set(font.getCharacterSet());

	return Array.from(String(text || ''))
		.map((character) => {
			if (character === '\n' || character === '\r') {
				return character;
			}

			return supportedCodePoints.has(character.codePointAt(0)) ? character : '?';
		})
		.join('');
};

const wrapText = ({ text, font, fontSize, maxWidth }) => {
	const lines = [];
	const rawLines = makeTextSafeForFont({ text, font }).split(/\r?\n/);

	rawLines.forEach((rawLine) => {
		const words = rawLine.split(/\s+/).filter(Boolean);
		if (!words.length) {
			lines.push('');
			return;
		}

		let currentLine = '';
		words.forEach((word) => {
			const nextLine = currentLine ? `${currentLine} ${word}` : word;
			const nextWidth = font.widthOfTextAtSize(nextLine, fontSize);

			if (nextWidth <= maxWidth || !currentLine) {
				currentLine = nextLine;
				return;
			}

			lines.push(currentLine);
			currentLine = word;
		});

		if (currentLine) {
			lines.push(currentLine);
		}
	});

	return lines;
};

export const generateCompanyDocumentFormPdfBytes = async ({
	title,
	description,
	questionAnswers,
	employeeName,
	answeredAt = new Date(),
}) => {
	const pdfDoc = await PDFDocument.create();
	const resolvedTitle = String(title || 'Company form response').trim();
	if (resolvedTitle) {
		pdfDoc.setTitle(resolvedTitle);
	}
	const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
	const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
	const maxWidth = A4_WIDTH - (PAGE_MARGIN * 2);

	let page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
	let cursorY = A4_HEIGHT - PAGE_MARGIN;

	const drawLines = ({ lines, font, fontSize = DEFAULT_FONT_SIZE, lineHeight = LINE_HEIGHT }) => {
		lines.forEach((line) => {
			page.drawText(line, {
				x: PAGE_MARGIN,
				y: cursorY,
				size: fontSize,
				font,
			});
			cursorY -= lineHeight;
		});
	};

	const ensureRoom = (requiredHeight = LINE_HEIGHT) => {
		if (cursorY - requiredHeight > MIN_BOTTOM_MARGIN) {
			return;
		}

		page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
		cursorY = A4_HEIGHT - PAGE_MARGIN;
	};

	ensureRoom(HEADER_FONT_SIZE + SECTION_SPACING);
	drawLines({
		lines: wrapText({
			text: resolvedTitle,
			font: boldFont,
			fontSize: HEADER_FONT_SIZE,
			maxWidth,
		}),
		font: boldFont,
		fontSize: HEADER_FONT_SIZE,
		lineHeight: HEADER_FONT_SIZE + 2,
	});
	cursorY -= 2;

	const completedAtLabel = (() => {
		try {
			return new Date(answeredAt).toLocaleString();
		}
		catch (error) {
			return String(answeredAt || '');
		}
	})();

	const metaLines = [
		`Employee: ${String(employeeName || '').trim() || 'Unknown'}`,
		`Completed: ${completedAtLabel}`,
	].map(text => makeTextSafeForFont({ text, font: regularFont }));
	ensureRoom((metaLines.length * LINE_HEIGHT) + SECTION_SPACING);
	drawLines({ lines: metaLines, font: regularFont });
	cursorY -= SECTION_SPACING;

	const formDescription = String(description || '').trim();
	if (formDescription) {
		const descriptionLines = wrapText({
			text: formDescription,
			font: regularFont,
			fontSize: DEFAULT_FONT_SIZE,
			maxWidth,
		});
		ensureRoom((descriptionLines.length * LINE_HEIGHT) + SECTION_SPACING);
		drawLines({
			lines: descriptionLines,
			font: regularFont,
			fontSize: DEFAULT_FONT_SIZE,
			lineHeight: LINE_HEIGHT,
		});
		cursorY -= SECTION_SPACING;
	}

	(questionAnswers || []).forEach((answerRecord, index) => {
		const questionLabel = String(answerRecord?.label || '').trim() || `Question ${index + 1}`;
		const answerValue = normalizeAnswerValue(answerRecord?.value);
		const comment = String(answerRecord?.comment || '').trim();

		const questionLines = wrapText({
			text: `${index + 1}. ${questionLabel}`,
			font: boldFont,
			fontSize: QUESTION_FONT_SIZE,
			maxWidth,
		});
		const answerLines = wrapText({
			text: `Answer: ${answerValue}`,
			font: regularFont,
			fontSize: DEFAULT_FONT_SIZE,
			maxWidth,
		});
		const commentLines = comment
			? wrapText({
				text: `Comment: ${comment}`,
				font: regularFont,
				fontSize: DEFAULT_FONT_SIZE,
				maxWidth,
			})
			: [];

		const requiredHeight = (
			(questionLines.length * (QUESTION_FONT_SIZE + 1)) +
			(answerLines.length * LINE_HEIGHT) +
			(commentLines.length * LINE_HEIGHT) +
			QUESTION_SPACING +
			SECTION_SPACING
		);
		ensureRoom(requiredHeight);

		drawLines({
			lines: questionLines,
			font: boldFont,
			fontSize: QUESTION_FONT_SIZE,
			lineHeight: QUESTION_FONT_SIZE + 1,
		});
		cursorY -= 2;

		drawLines({
			lines: answerLines,
			font: regularFont,
			fontSize: DEFAULT_FONT_SIZE,
			lineHeight: LINE_HEIGHT,
		});

		if (commentLines.length) {
			drawLines({
				lines: commentLines,
				font: regularFont,
				fontSize: DEFAULT_FONT_SIZE,
				lineHeight: LINE_HEIGHT,
			});
		}

		cursorY -= SECTION_SPACING;
	});

	return pdfDoc.save();
};
